import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { ordersApi, paymentPlansApi, installmentSettingsApi } from '../api/client';
import { useTranslation } from '../i18n/useTranslation';

const PAYMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
];

const PaymentBuilder = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [parts, setParts] = useState([
    { method: 'PIX', amount: '', installments: '', installmentMode: 'fixed', label: '', dueDate: '' }
  ]);
  
  // Installment settings from database
  const [installmentSettings, setInstallmentSettings] = useState(null);
  const [maxInstallmentsNoInterest, setMaxInstallmentsNoInterest] = useState(6);
  const [interestRate, setInterestRate] = useState(2.99);
  const [maxInstallments, setMaxInstallments] = useState(24);
  const [tiers, setTiers] = useState([]);
  
  // Boleto settings from database
  const [boletoSettings, setBoletoSettings] = useState({ defaultDueDays: 3, maxDueDays: 4 });

  useEffect(() => {
    loadInstallmentSettings();
    loadBoletoSettings();
    if (!order) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const response = await ordersApi.getOrder(orderId);
      setOrder(response.data.data);
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadInstallmentSettings = async () => {
    try {
      const response = await installmentSettingsApi.getActive();
      if (response.data.success && response.data.data) {
        const settings = response.data.data;
        setInstallmentSettings(settings);
        setMaxInstallmentsNoInterest(settings.interest_free_limit || 6);
        setMaxInstallments(settings.max_installments || 24);
        setTiers(settings.tiers || []);
        
        // Calculate interest rate from first tier with interest
        if (settings.tiers && settings.tiers.length > 0) {
          const firstInterestTier = settings.tiers.find(t => t.interestRate > 0);
          if (firstInterestTier) {
            setInterestRate(parseFloat(firstInterestTier.interestRate));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load installment settings, using defaults:', err);
    }
  };

  const loadBoletoSettings = async () => {
    try {
      const response = await fetch('/api/settings/boleto');
      const data = await response.json();
      if (data.success && data.data) {
        setBoletoSettings(data.data);
      }
    } catch (err) {
      console.error('Failed to load boleto settings, using defaults:', err);
    }
  };

  const addPart = () => {
    const defaultDueDate = getDefaultBoletoDate();
    setParts([...parts, { method: 'PIX', amount: '', installments: '', installmentMode: 'fixed', label: '', dueDate: defaultDueDate }]);
  };

  const removePart = (index) => {
    if (parts.length > 1) {
      setParts(parts.filter((_, i) => i !== index));
    }
  };

  const updatePart = (index, field, value) => {
    const newParts = [...parts];
    
    // When changing to boleto, set default due date
    if (field === 'method' && value === 'BOLETO') {
      newParts[index].dueDate = getDefaultBoletoDate();
    }
    
    newParts[index][field] = value;
    setParts(newParts);
  };

  const getDefaultBoletoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + boletoSettings.defaultDueDays);
    return date.toISOString().split('T')[0];
  };

  const getMaxBoletoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + boletoSettings.maxDueDays);
    return date.toISOString().split('T')[0];
  };

  const validateBoletoDate = (dateString) => {
    if (!dateString) return false;
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + boletoSettings.maxDueDays);
    maxDate.setHours(23, 59, 59, 999);
    
    return selectedDate >= today && selectedDate <= maxDate;
  };

  const calculateTotal = () => {
    return parts.reduce((sum, part) => {
      const amount = parseFloat(part.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const getRemainingAmount = () => {
    if (!order) return 0;
    return order.total - calculateTotal();
  };

  const calculateFinalAmount = (part) => {
    const amount = parseFloat(part.amount) || 0;
    if (part.method !== 'CREDIT_CARD' || !part.installments) {
      return amount;
    }
    
    const installments = parseInt(part.installments);
    
    // Use tier-based calculation if available
    if (tiers && tiers.length > 0) {
      const tier = tiers.find(
        t => installments >= t.minInstallments && installments <= t.maxInstallments
      );
      
      if (tier && tier.interestRate > 0) {
        const rate = parseFloat(tier.interestRate) / 100;
        const finalAmount = amount * Math.pow(1 + rate, installments);
        return finalAmount;
      }
      return amount; // No interest for this tier
    }
    
    // Fallback to simple calculation
    if (installments <= maxInstallmentsNoInterest) {
      return amount;
    }
    
    const rate = interestRate / 100;
    const finalAmount = amount * Math.pow(1 + rate, installments - maxInstallmentsNoInterest);
    return finalAmount;
  };

  const calculateInstallmentValue = (part) => {
    const finalAmount = calculateFinalAmount(part);
    const installments = parseInt(part.installments) || 1;
    return finalAmount / installments;
  };

  const isValidForSubmission = () => {
    if (!order) return false;
    
    const allPartsValid = parts.every(part => {
      if (!part.method || !part.amount) return false;
      if (parseFloat(part.amount) <= 0) return false;
      if (part.method === 'CREDIT_CARD' && !part.installments) return false;
      if (part.method === 'BOLETO' && !validateBoletoDate(part.dueDate)) return false;
      if (part.method === 'BOLETO' && !part.dueDate) return false;
      return true;
    });

    const remaining = Math.abs(getRemainingAmount());
    return allPartsValid && remaining < 0.01;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidForSubmission()) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        order_id: order.id,
        salesman_id: 'user-' + Date.now(),
        parts: parts.map(part => ({
          method: part.method,
          amount: parseFloat(part.amount),
          installments: part.method === 'CREDIT_CARD' ? parseInt(part.installments) : undefined,
          installmentMode: part.method === 'CREDIT_CARD' ? part.installmentMode : undefined,
          dueDate: part.method === 'BOLETO' ? part.dueDate : undefined,
        })),
      };

      const response = await paymentPlansApi.create(payload);
      navigate(`/payment-plans/${response.data.data.plan.id}`, {
        state: { plan: response.data.data }
      });
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-sm text-red-800">{t('common.error')}</p>
      </div>
    );
  }

  const remaining = getRemainingAmount();
  const isBalanced = Math.abs(remaining) < 0.01;

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">{t('paymentBuilder.title')}</h1>
        <p className="mt-2 text-sm text-gray-400">
          {t('orderLookup.orderNumber')} #{order.number} - {order.customer.name}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-600">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-white">{t('paymentBuilder.paymentParts')}</h2>
              <button
                onClick={addPart}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('paymentBuilder.addPart')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {parts.map((part, index) => (
                <div key={index} className="border border-dark-500 rounded-lg p-4 bg-dark-600">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-gray-300">
                      {t('paymentBuilder.part')} {index + 1}
                    </span>
                    {parts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePart(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">
                        {t('paymentBuilder.method')}
                      </label>
                      <select
                        value={part.method}
                        onChange={(e) => updatePart(index, 'method', e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-dark-500 bg-dark-700 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                      >
                        {PAYMENT_METHODS.map(method => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300">
                        {t('paymentBuilder.amount')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={part.amount}
                        onChange={(e) => updatePart(index, 'amount', e.target.value)}
                        className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>

                    {part.method === 'BOLETO' && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Data de Vencimento
                        </label>
                        <input
                          type="date"
                          value={part.dueDate}
                          onChange={(e) => updatePart(index, 'dueDate', e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          max={getMaxBoletoDate()}
                          className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                            part.dueDate && !validateBoletoDate(part.dueDate)
                              ? 'border-red-500 bg-dark-700 text-white'
                              : 'border-dark-500 bg-dark-700 text-white'
                          }`}
                        />
                        {part.dueDate && !validateBoletoDate(part.dueDate) ? (
                          <p className="mt-1 text-xs text-red-400">
                            Data deve estar entre hoje e {boletoSettings.maxDueDays} dias
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-400">
                            Vencimento padrão: {boletoSettings.defaultDueDays} dias (máximo: {boletoSettings.maxDueDays} dias)
                          </p>
                        )}
                      </div>
                    )}

                    {part.method === 'CREDIT_CARD' && (
                      <>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Tipo de Parcelamento
                          </label>
                          <div className="flex gap-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`installmentMode-${index}`}
                                value="fixed"
                                checked={part.installmentMode === 'fixed'}
                                onChange={(e) => updatePart(index, 'installmentMode', e.target.value)}
                                className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-500"
                              />
                              <span className="ml-2 text-sm text-gray-300">
                                <span className="font-medium">Parcelamento Fixo</span>
                                <span className="block text-xs text-gray-400">Cliente paga exatamente este número de parcelas</span>
                              </span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`installmentMode-${index}`}
                                value="max"
                                checked={part.installmentMode === 'max'}
                                onChange={(e) => updatePart(index, 'installmentMode', e.target.value)}
                                className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-500"
                              />
                              <span className="ml-2 text-sm text-gray-300">
                                <span className="font-medium">Até X Parcelas</span>
                                <span className="block text-xs text-gray-400">Cliente escolhe de 1 até este número</span>
                              </span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300">
                            {part.installmentMode === 'fixed' ? 'Número de Parcelas' : 'Máximo de Parcelas'}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max={maxInstallments}
                            value={part.installments}
                            onChange={(e) => updatePart(index, 'installments', e.target.value)}
                            className="mt-1 block w-full border border-dark-500 bg-dark-700 text-white rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder="1"
                          />
                          {part.installments && part.amount && (
                            <div className="mt-2 p-2 bg-dark-800 border border-dark-500 rounded-md">
                              {part.installmentMode === 'fixed' ? (
                                <>
                                  <p className="text-xs font-medium text-blue-400">
                                    {parseInt(part.installments)}x de {order.currency} {calculateInstallmentValue(part).toFixed(2)}
                                  </p>
                                  {parseInt(part.installments) > maxInstallmentsNoInterest && (
                                    <p className="text-xs text-blue-300 mt-1">
                                      Total com juros: {order.currency} {calculateFinalAmount(part).toFixed(2)}
                                      <span className="text-blue-400"> ({interestRate}% a.m.)</span>
                                    </p>
                                  )}
                                  {parseInt(part.installments) <= maxInstallmentsNoInterest && (
                                    <p className="text-xs text-green-400 mt-1">
                                      Sem juros
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <p className="text-xs font-medium text-blue-400">
                                    Cliente escolhe: 1x até {parseInt(part.installments)}x
                                  </p>
                                  <p className="text-xs text-blue-300 mt-1">
                                    Valor por parcela varia conforme escolha do cliente
                                  </p>
                                  {parseInt(part.installments) > maxInstallmentsNoInterest && (
                                    <p className="text-xs text-orange-400 mt-1">
                                      Parcelas acima de {maxInstallmentsNoInterest}x terão juros de {interestRate}% a.m.
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {error && (
                <div className="bg-red-900 bg-opacity-50 border border-red-700 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <p className="ml-3 text-sm text-red-200">{error}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!isValidForSubmission() || submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      {t('paymentBuilder.creating')}
                    </>
                  ) : (
                    t('paymentBuilder.createPlan')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-600 sticky top-6">
            <h3 className="text-lg font-medium text-white mb-4">{t('paymentBuilder.summary')}</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">{t('orderLookup.total')}</span>
                <span className="font-medium text-white">
                  {order.currency} {order.total.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-400">{t('paymentBuilder.totalAmount')}</span>
                <span className="font-medium text-white">
                  {order.currency} {calculateTotal().toFixed(2)}
                </span>
              </div>

              <div className="border-t border-dark-600 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-white">{t('paymentBuilder.remaining')}</span>
                  <div className="flex items-center">
                    <span className={`font-bold ${
                      isBalanced ? 'text-green-400' : remaining > 0 ? 'text-orange-400' : 'text-red-400'
                    }`}>
                      {order.currency} {Math.abs(remaining).toFixed(2)}
                    </span>
                    {isBalanced && (
                      <CheckCircle className="h-5 w-5 text-green-400 ml-2" />
                    )}
                  </div>
                </div>
                {!isBalanced && (
                  <p className="mt-2 text-xs text-gray-400">
                    {remaining > 0 
                      ? 'Adicione mais parcelas para atingir o total do pedido'
                      : 'Reduza os valores das parcelas para atingir o total do pedido'}
                  </p>
                )}
              </div>

              <div className="border-t border-dark-600 pt-3">
                <p className="text-xs text-gray-400">
                  <strong>Nota:</strong> Até {maxInstallmentsNoInterest} parcelas sem juros.
                  Acima disso, juros serão aplicados.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentBuilder;
